import React, { useState, useEffect } from 'react';
import { useFieldArray } from 'react-hook-form';

import CriticalElementFormView from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element-form/critical-element-form-view';
import toast from 'components/partials/toast/toast';

import type { CriticalElementSummaryRequest } from 'api/generated/schemas';

import { generateCriticalElementSummary } from 'api/generated/critical-element-summary-controller/critical-element-summary-controller';

import { useCriticalElementForm } from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/context/context';

import type { CriticalElement as CriticalElementType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface CriticalElementFormProps {
  data: CriticalElementType | null;
  isEditing: boolean;
  isCreation: boolean;
  userId: string | undefined;
}

const CriticalElementForm = ({
  data,
  isEditing,
  isCreation,
  userId,
}: CriticalElementFormProps): React.JSX.Element => {
  const [generationLoading, setGenerationLoading] = useState<boolean>(false);

  const { control, setValue, getValues, clearErrors } =
    useCriticalElementForm();
  const isDisabled = (Boolean(data) && !isEditing) || generationLoading;

  const {
    fields: detailFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'details',
  });

  useEffect(() => {
    if (data?.details?.length === 0 && isCreation) {
      append({ category: '', focusArea: '', subArea: '', options: [] });
    }
  }, [data?.details?.length, isCreation, append]);

  const generateSummary = async (): Promise<void> => {
    if (!userId) return;

    setGenerationLoading(true);

    const formData = getValues();

    const elementData = {
      ...(data ?? {}),
      metric: formData.metric,
      stage: formData.stage,
      relativeToLastYear: formData.relativeToLastYear,
      details: formData.details.map(
        (item: { options?: unknown[] | string }) => ({
          ...item,
          options: item.options
            ? ([] as unknown[]).concat(item.options as never)
            : [],
        })
      ),
    };

    try {
      const summaryResponse = await generateCriticalElementSummary(
        userId,
        elementData as CriticalElementSummaryRequest
      );

      if (summaryResponse?.summary) {
        setValue('summary', summaryResponse.summary);
        clearErrors('summary');
      }
    } catch (error) {
      toast.error({ title: 'Error while generating AI description' });
      console.error('Error while generating AI description', error);
    } finally {
      setGenerationLoading(false);
    }
  };

  const initialShowNote = ((data?.note as string | undefined)?.length ?? 0) > 0;

  return (
    <CriticalElementFormView
      detailFields={detailFields}
      append={append}
      remove={remove}
      isDisabled={isDisabled}
      control={control}
      clearErrors={clearErrors}
      generateSummary={generateSummary}
      generationLoading={generationLoading}
      initialShowNote={initialShowNote}
      setValue={setValue}
    />
  );
};

export default CriticalElementForm;
